/**
 * Whether the signed-in person holds a permission, as far as the UI can tell (SC-439).
 *
 * Pure, so the route guard and the React hooks give the same answer. The server and cloud are the
 * real gates; this only decides what to show.
 */
import { getPermissionEntry, isPermissionKey } from '../../../types/rbac/catalogue'
import { RBAC_SURFACES, type RbacSurface } from '../../../types/rbac/surfaces'
import type { RbacMe } from '../services/rbacService'
import type { RbacStatus } from '../stores/rbacStore'

/** `pending` means the answer is still coming: show neither the gated control nor a "no". */
export type PermissionVerdict = boolean | 'pending'

export interface RbacSnapshot {
  status: RbacStatus
  me: RbacMe | null
}

/**
 * Any one of `keys` is enough. `elevated` is a server-verified platform admin: the server lets
 * them through every company check, so the UI does too, except for the owner-only keys, which no
 * bypass reaches (only the Owner may transfer or delete a workspace).
 */
export function resolvePermission(
  keys: string | readonly string[],
  rbac: RbacSnapshot,
  elevated: boolean
): PermissionVerdict {
  const wanted = (Array.isArray(keys) ? keys : [keys]).filter(isPermissionKey)
  if (wanted.length === 0) return false

  const answer = rbac.status === 'ready' ? rbac.me : null
  const held = new Set(answer?.permissions ?? [])
  for (const key of wanted) {
    const grantable = getPermissionEntry(key)?.grantable !== false
    if (!grantable) {
      if (answer?.isOwner && held.has(key)) return true
    } else if (elevated || held.has(key)) {
      return true
    }
  }
  return rbac.status === 'idle' || rbac.status === 'loading' ? 'pending' : false
}

const SURFACE_BY_ID = new Map<string, RbacSurface>(RBAC_SURFACES.map((s) => [s.id, s]))

/**
 * The permission a UI surface needs, from the shared surface list, so a control and the
 * catalogue's "this unlocks ..." can't name different keys. An unknown id is a bug: it is
 * reported and the control stays hidden.
 */
export function surfaceRequirement(surfaceId: string): RbacSurface['requires'] | null {
  const surface = SURFACE_BY_ID.get(surfaceId)
  if (!surface) {
    console.error(`[rbac] Unknown surface id: ${surfaceId}. Add it to types/rbac/surfaces.ts.`)
    return null
  }
  return surface.requires
}
